import Link from "next/link";

import { css, styled } from "@mui/material";

import { getColor } from "@hooks";
import { cssPropertiesToString, WorkingArea } from "@utils";

import { workingAreaToBaseColorDict } from "./utils";

export type ArticleCardSize = "small" | "medium" | "big";

interface ArticleCardContainerProps {
  size: ArticleCardSize;
}

const onHoverTransitionTime = "0.8s";

export const ArticleCardContainer = styled(Link, {
  shouldForwardProp: prop => prop !== "size",
})<ArticleCardContainerProps>(({ size, theme }) => {
  const isLightTheme = theme.palette.mode === "light";

  return css`
    display: flex;
    flex-direction: ${size === "big" ? "row" : "column"};
    gap: ${size === "small" ? "8px" : size === "medium" ? "24px" : "32px"};

    flex: 1;

    cursor: pointer;

    &:hover {
      & .article-card-header {
        text-decoration: underline;
        transition: text-decoration-color ${onHoverTransitionTime};
      }

      & .wrapper-1 {
        border-color: ${isLightTheme
          ? "rgba(14, 18, 27, 0.1)"
          : "rgba(255, 255, 255, 0.1)"};
      }
      & .wrapper-2 {
        border-color: ${isLightTheme
          ? "rgba(14, 18, 27, 0.2)"
          : "rgba(255, 255, 255, 0.2)"};
      }
      & .wrapper-3 {
        border-color: ${isLightTheme
          ? "rgba(14, 18, 27, 0.3)"
          : "rgba(255, 255, 255, 0.3)"};
      }

      & .wrapper-1,
      & .wrapper-2,
      & .wrapper-3 {
        transition: border-color ${onHoverTransitionTime};
      }
    }
  `;
});

export const ImageWrapper1 = styled("div")(css`
  border: 1px solid transparent;
  padding: 2px;
  border-radius: 11px;
  min-width: 60%;
`);

export const ImageWrapper2 = styled("div")(css`
  border: 1px solid transparent;
  padding: 3px;
  border-radius: 7px;
`);

export const ImageWrapper3 = styled("div")(css`
  border: 1px solid transparent;
  border-radius: 4px;

  position: relative;
  overflow: hidden;

  aspect-ratio: 9/5;
`);

interface ArticleCardAreaProps {
  area: WorkingArea;
}

export const ArticleCardArea = styled("p", {
  shouldForwardProp: prop => prop !== "size",
})<ArticleCardAreaProps>(
  ({ theme, area }) => css`
    ${cssPropertiesToString(theme.typography.paragraph_small_regular)}
    color: ${workingAreaToBaseColorDict(theme)[area]};
  `,
);

interface ArticleCardHeaderProps {
  size: ArticleCardSize;
}

export const ArticleCardHeader = styled("p", {
  shouldForwardProp: prop => prop !== "size",
})<ArticleCardHeaderProps>(
  ({ theme, size }) => css`
    ${cssPropertiesToString(
      size === "small"
        ? theme.typography.paragraph_xxlarge_medium
        : size === "medium"
          ? theme.typography.heading_h6_medium_desktop
          : theme.typography.heading_h2_bold_desktop,
    )}
    color: ${getColor(c => c.tokens.text["strong-950"], theme)};

    height: ${size === "big" ? "96px" : "64px"};
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
    overflow: hidden;
    text-overflow: ellipsis;

    text-decoration-color: transparent;
  `,
);

interface ArticleCardDescriptionProps {
  size: ArticleCardSize;
}

export const ArticleCardDescription = styled("p", {
  shouldForwardProp: prop => prop !== "size",
})<ArticleCardDescriptionProps>(
  ({ theme, size }) => css`
    ${cssPropertiesToString(theme.typography.paragraph_medium_regular)}
    color: ${getColor(c => c.tokens.text["sub-600"], theme)};

    height: ${size === "small" ? "48px" : "96px"};
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: ${size === "small" ? 2 : 4};
    overflow: hidden;
    text-overflow: ellipsis;
  `,
);

export const ArticleCardDate = styled("p")(
  ({ theme }) => css`
    ${cssPropertiesToString(theme.typography.paragraph_small_regular)}
    color: ${getColor(c => c.tokens.text["strong-950"], theme)};
  `,
);

export const ArticleCardAuthor = styled("p")(
  ({ theme }) => css`
    ${cssPropertiesToString(theme.typography.paragraph_small_regular)}
    color: ${getColor(c => c.tokens.text["sub-600"], theme)};
  `,
);
